import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";

// "ResizeObserver loop completed with undelivered notifications" is a
// harmless browser warning, not a crash — but CRA's dev error-overlay
// registers its window "error" listener before this file runs (it's
// injected as an earlier webpack entry), so we can't reliably intercept
// the event afterwards. Fix it at the source instead: defer every
// ResizeObserver callback to the next animation frame so the same-frame
// resize loop that triggers the browser's warning never happens.
if (typeof window.ResizeObserver !== "undefined") {
  const OriginalResizeObserver = window.ResizeObserver;
  window.ResizeObserver = class extends OriginalResizeObserver {
    constructor(callback) {
      super((entries, observer) => {
        window.requestAnimationFrame(() => callback(entries, observer));
      });
    }
  };
}
// Backstop for any occurrence that still slips through.
const RESIZE_OBSERVER_LOOP_MSG =
  "ResizeObserver loop completed with undelivered notifications.";
window.addEventListener("error", (e) => {
  if (e.message === RESIZE_OBSERVER_LOOP_MSG) {
    e.stopImmediatePropagation();
  }
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// Register service worker — production builds only. CRA's dev server
// already does its own live/hot reload; registering the offline-cache SW
// on top of that actively fights it — the SW's network-first fetch
// handler and periodic reload-on-controllerchange logic have caused
// exactly the "I fixed the code but the browser is still showing the old
// broken behavior" confusion during active dev iteration. Unregister any
// SW a dev session previously picked up, too, so this doesn't require a
// manual "clear site data" on every dev machine that already has one
// installed from an earlier session.
if ("serviceWorker" in navigator && process.env.NODE_ENV === "production") {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js")
      .then((reg) => {
        if (reg.waiting) {
          window.dispatchEvent(new CustomEvent("tulsi:sw-update-ready", { detail: reg }));
        }
        reg.addEventListener("updatefound", () => {
          const nw = reg.installing;
          if (!nw) return;
          nw.addEventListener("statechange", () => {
            if (nw.state === "installed" && navigator.serviceWorker.controller) {
              window.dispatchEvent(new CustomEvent("tulsi:sw-update-ready", { detail: reg }));
            }
          });
        });
      })
      .catch(() => {
        /* SW registration failed — app still works */
      });

    // If a fresh SW takes over the page, force one reload so we're guaranteed
    // running the new bundle. Session flag prevents reload loops.
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      const flag = "tulsi_sw_reloaded_at";
      const last = Number(sessionStorage.getItem(flag) || "0");
      const now = Date.now();
      if (now - last > 10_000) {
        sessionStorage.setItem(flag, String(now));
        window.location.reload();
      }
    });
  });
} else if ("serviceWorker" in navigator) {
  // Dev mode — actively remove any SW + its caches left over from a
  // previous run (e.g. this same origin was built/served as production
  // before, or a prior dev session had SW registration unconditional).
  navigator.serviceWorker.getRegistrations().then((regs) => {
    regs.forEach((r) => r.unregister());
  });
  if (window.caches) {
    caches.keys().then((keys) => keys.forEach((k) => caches.delete(k)));
  }
}
